import { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import JobCard from '../job-board/components/JobCard';
import FilterPanel from '../job-board/components/FilterPanel';
import SearchBar from '../job-board/components/SearchBar';
import TagFilter from '../job-board/components/TagFilter';
import SavedSearches from '../job-board/components/SavedSearches';
import JobDetailModal from '../job-board/components/JobDetailModal';
import QuickApplyModal from '../job-board/components/QuickApplyModal';
import ApplicationTracker from '../job-board/components/ApplicationTracker';
import { jobsService, applicationsService } from '../../services/supabaseService';

const FindWork = () => {
  const [viewMode, setViewMode] = useState('card');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyingJob, setApplyingJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showApplications, setShowApplications] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);

  const [filters, setFilters] = useState({
    category: 'all',
    location: 'all',
    budget: { min: '', max: '' },
    datePosted: 'all',
    verifiedOnly: false,
    urgentOnly: false,
    minRating: null,
    complexity: 'all'
  });

  const availableTags = [
    'Plumbing', 'Electrical', 'Carpentry', 'Cleaning', 'Babysitting',
    'Tutoring', 'Driving', 'Cooking', 'Laundry', 'Errands',
    'Tech Support', 'Beauty Services', 'Painting', 'Gardening',
    'Pet Care', 'Moving', 'Repair', 'Installation', 'Maintenance'
  ];

  useEffect(() => {
    fetchJobs();
  }, [filters, selectedTags]);

  useEffect(() => {
    if (searchQuery?.length > 0) {
      const suggestions = generateSearchSuggestions(searchQuery);
      setSearchSuggestions(suggestions);
    } else {
      setSearchSuggestions([]);
    }
  }, [searchQuery]);

  const generateSearchSuggestions = (query) => {
    const lowerQuery = query?.toLowerCase();
    const allSuggestions = [
      ...availableTags,
      'Emergency plumbing',
      'Licensed electrician',
      'Experienced carpenter',
      'Professional cleaning',
      'Certified babysitter',
      'Math tutor',
      'Driver with car',
      'Home cooking',
      'Laundry service'
    ];

    return allSuggestions
      ?.filter((s) => s?.toLowerCase()?.includes(lowerQuery))
      ?.slice(0, 6);
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await jobsService?.getJobs({
        category: filters?.category,
        location: filters?.location,
        budgetMin: filters?.budget?.min,
        budgetMax: filters?.budget?.max,
        datePosted: filters?.datePosted,
        verifiedOnly: filters?.verifiedOnly,
        urgentOnly: filters?.urgentOnly
      });
      setJobs(data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    fetchJobs();
  };

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
      prev?.includes(tag) ? prev?.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setShowDetailModal(true);
  };

  const handleQuickApply = (job) => {
    setApplyingJob(job);
    setShowApplyModal(true);
  };

  const handleApplicationSubmit = async (applicationData) => {
    try {
      await applicationsService?.createApplication(applicationData);
      setSuccessMessage('Application submitted successfully!');
      setShowApplyModal(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchJobs();
    } catch (err) {
      console.error('Error submitting application:', err);
      alert('Failed to submit application. Please try again.');
    }
  };

  const handleSaveJob = (jobId) => {
    setSavedJobs((prev) =>
      prev?.includes(jobId) ? prev?.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      location: 'all',
      budget: { min: '', max: '' },
      datePosted: 'all',
      verifiedOnly: false,
      urgentOnly: false,
      minRating: null,
      complexity: 'all'
    });
    setSelectedTags([]);
    setSearchQuery('');
  };

  const sortedJobs = [...jobs]?.sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b?.postedDate) - new Date(a?.postedDate);
      case 'budget-high':
        return b?.budgetMax - a?.budgetMax;
      case 'budget-low':
        return a?.budgetMin - b?.budgetMin;
      case 'applicants':
        return a?.applicantCount - b?.applicantCount;
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      <Header />
      
      <main className="pt-[60px] lg:pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-[16px] lg:px-[24px] py-[24px] lg:py-[32px]">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="lg:text-3xl font-heading font-bold text-foreground mb-2 text-base">
                  Find Work Opportunities
                </h1>
                <p className="lg:text-base text-muted-foreground text-sm">
                  Discover jobs matching your skills and availability
                </p>
              </div>
              <Button
                variant="outline"
                size="default"
                onClick={() => setShowApplications(!showApplications)}
                iconName="FileText"
                iconPosition="left"
                className="border-[#FF6A00] text-[#FF6A00] hover:bg-[#FF6A00]/10 text-xs"
              >
                My Applications
              </Button>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2 mb-4">
                <Icon name="CheckCircle" size={20} color="#16a34a" />
                <span className="text-sm font-medium">{successMessage}</span>
              </div>
            )}
          </div>

          {/* Search and Tags */}
          <div className="mb-6 space-y-4">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={handleSearch}
              suggestions={searchSuggestions}
            />
            <TagFilter
              tags={availableTags}
              selectedTags={selectedTags}
              onTagToggle={handleTagToggle}
              onTagsChange={setSelectedTags}
            />
            <SavedSearches 
              currentFilters={filters}
              onLoadSearch={(savedFilters) => {
                setFilters(savedFilters);
                fetchJobs();
              }}
            />
          </div>

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Filter Sidebar */}
            <aside className="lg:w-[280px] flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-[76px]">
                <FilterPanel
                  filters={filters}
                  onFilterChange={setFilters}
                  onClearFilters={handleClearFilters}
                />
              </div>
            </aside>

            {/* Job Listings */}
            <div className="flex-1 min-w-0">
              {/* Controls */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Icon name="Briefcase" size={20} color="#5B3DF5" />
                  <span className="text-sm font-medium text-foreground">
                    {loading ? 'Loading...' : `${sortedJobs?.length} jobs available`}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e?.target?.value)}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5B3DF5]/20"
                  >
                    <option value="newest">Newest First</option>
                    <option value="budget-high">Highest Budget</option>
                    <option value="budget-low">Lowest Budget</option>
                    <option value="applicants">Fewest Applicants</option>
                  </select>

                  <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`p-2 rounded transition-smooth ${
                        viewMode === 'card' ?'bg-[#FF6A00] text-white' :'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name="LayoutGrid" size={16} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-smooth ${
                        viewMode === 'list' ?'bg-[#FF6A00] text-white' :'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name="List" size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Job Cards */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#1E73BE] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading opportunities...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-8 rounded-xl text-center">
                  <Icon name="AlertCircle" size={48} color="#dc2626" className="mx-auto mb-4" />
                  <p className="font-medium mb-2">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchJobs}
                    className="border-red-600 text-red-600 hover:bg-red-50"
                  >
                    Try Again
                  </Button>
                </div>
              ) : sortedJobs?.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-100 px-6 py-12 text-center">
                  <Icon name="Search" size={48} color="#9CA3AF" className="mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No jobs found
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or search terms
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearFilters}
                    iconName="X"
                    className="border-[#FF6A00] text-[#FF6A00]"
                  >
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div
                  className={`grid gap-4 ${
                    viewMode === 'card' ?'grid-cols-2 lg:grid-cols-3' :'grid-cols-1'
                  }`}
                >
                  {sortedJobs?.map((job) => (
                    <JobCard
                      key={job?.id}
                      job={job}
                      onViewDetails={handleViewDetails}
                      onQuickApply={handleQuickApply}
                      viewMode={viewMode}
                      isSaved={savedJobs?.includes(job?.id)}
                      onSave={() => handleSaveJob(job?.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {showDetailModal && selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => setShowDetailModal(false)}
          onApply={() => {
            setShowDetailModal(false);
            handleQuickApply(selectedJob);
          }}
        />
      )}

      {showApplyModal && applyingJob && (
        <QuickApplyModal
          job={applyingJob}
          onClose={() => setShowApplyModal(false)}
          onSubmit={handleApplicationSubmit}
        />
      )}

      {showApplications && (
        <ApplicationTracker 
          applications={[]}
          onClose={() => setShowApplications(false)} 
        />
      )}
    </div>
  );
};

export default FindWork;